/*
 * Walking a form one field at a time.
 *
 * The flow defines a `form` step as a set of fields submitted together, and
 * the server still receives them together — that contract is unchanged. What
 * changes is the ASKING: on a chat surface six labelled boxes with a Continue
 * button underneath is a web form pasted into a conversation, so this renderer
 * asks the fields one by one, echoes each answer into the transcript, and posts
 * the whole payload when the last one is answered.
 *
 * The pure decisions live here, away from React, because each has a wrong
 * answer that ships silently: skipping a required field, re-asking a question
 * the server accepted, or landing the customer on the wrong field after a
 * rejection. See walk.test.ts.
 */

import type { Field } from '@blooq/flow'

/** A drafted answer set. Only strings; the server coerces. */
export type Draft = Record<string, string>

/**
 * How a field is asked, as a line in the conversation.
 *
 * "(optional)" is stripped because the dock says that with a Skip button, and
 * a bubble reading "Email (optional)" is a form label pretending to be speech.
 */
export function askOf(field: Field): string {
  return field.label.replace(/\s*\((optional)\)\s*$/i, '').trim()
}

/** True when the field holds a non-blank answer. */
export function isAnswered(field: Field, draft: Draft): boolean {
  return Boolean(draft[field.name]?.trim())
}

/** True when the customer may move on from this field. */
export function canLeave(field: Field, draft: Draft): boolean {
  return !field.required || isAnswered(field, draft)
}

/**
 * What the transcript shows as the customer's answer.
 *
 * For a choice the OPTION LABEL, never the value — `self` is a payload, and
 * "Myself" is what they tapped. Undefined when the field was skipped, so the
 * transcript shows the question and no reply rather than an empty bubble.
 */
export function echoOf(field: Field, draft: Draft): string | undefined {
  const value = draft[field.name]?.trim()
  if (!value) return undefined
  if (field.type === 'choice') {
    return field.options?.find((option) => option.value === value)?.label ?? value
  }
  return value
}

/**
 * Where to land after the server rejects a submission.
 *
 * The first field the server complained about, in the ORDER THE FIELDS ARE
 * ASKED — not the order of the error object, which is the server's business.
 * Null when none of the errors name a field we ask, in which case the walk
 * stays put and the error is shown against the step itself.
 */
export function firstError(fields: Field[], errors?: Record<string, string>): number | null {
  if (!errors) return null
  const index = fields.findIndex((field) => Boolean(errors[field.name]))
  return index === -1 ? null : index
}

/**
 * The next field to ask after `index` is answered, or null when the walk is
 * complete and the payload should be posted.
 */
export function nextIndex(fields: Field[], index: number): number | null {
  const next = index + 1
  return next < fields.length ? next : null
}

/** The payload for the step: every field, blanks included, so the server sees what was skipped. */
export function payloadOf(fields: Field[], draft: Draft): Draft {
  const payload: Draft = {}
  for (const field of fields) payload[field.name] = draft[field.name]?.trim() ?? ''
  return payload
}
